import React, { useMemo } from 'react';
import { Flame } from 'lucide-react';
import './TenureTracker.css';

// Badge image URLs
const BADGE_IMAGES = {
  rookie: 'https://customer-assets.emergentagent.com/job_a72ebe4e-56cc-40ab-ba42-98710f0295be/artifacts/fs8admzz_Rookie.png',
  regular: 'https://customer-assets.emergentagent.com/job_a72ebe4e-56cc-40ab-ba42-98710f0295be/artifacts/u8hwt6e3_Regular.png',
  founding: 'https://customer-assets.emergentagent.com/job_a72ebe4e-56cc-40ab-ba42-98710f0295be/artifacts/cubm4pyk_Founding.png',
  legacy: 'https://customer-assets.emergentagent.com/job_a72ebe4e-56cc-40ab-ba42-98710f0295be/artifacts/4ety6jdt_Legacy.png',
  legend: 'https://customer-assets.emergentagent.com/job_a72ebe4e-56cc-40ab-ba42-98710f0295be/artifacts/btwo6a7e_Legend.png'
};

// Tenure tier configuration
const TENURE_TIERS = {
  ROOKIE: { 
    id: 'rookie',
    name: 'Rookie Member', 
    minMonths: 0, 
    maxMonths: 2,
    image: BADGE_IMAGES.rookie
  },
  REGULAR: { 
    id: 'regular',
    name: 'Clubhouse Regular', 
    minMonths: 2, 
    maxMonths: 6,
    image: BADGE_IMAGES.regular
  },
  FOUNDING: { 
    id: 'founding',
    name: 'Founding Member', 
    minMonths: 6, 
    maxMonths: 18,
    image: BADGE_IMAGES.founding
  },
  LEGACY: { 
    id: 'legacy',
    name: 'Legacy Member', 
    minMonths: 18, 
    maxMonths: 24,
    image: BADGE_IMAGES.legacy
  },
  LEGEND: { 
    id: 'legend',
    name: 'Clubhouse Legend', 
    minMonths: 24, 
    maxMonths: Infinity,
    image: BADGE_IMAGES.legend
  }
};

// Calculate tenure from created date
export const calculateTenure = (createdAt) => {
  if (!createdAt) {
    return { months: 0, years: 0, days: 0 };
  }

  const created = new Date(createdAt);
  const now = new Date();
  
  let months = (now.getFullYear() - created.getFullYear()) * 12;
  months += now.getMonth() - created.getMonth();
  
  // Adjust if day of month hasn't been reached yet
  if (now.getDate() < created.getDate()) {
    months--;
  }
  
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  
  return { 
    months: Math.max(0, months), 
    years, 
    remainingMonths,
    days: Math.floor((now - created) / (1000 * 60 * 60 * 24))
  };
};

// Get current tier based on months
export const getTenureTier = (months) => {
  if (months >= 24) return TENURE_TIERS.LEGEND;
  if (months >= 18) return TENURE_TIERS.LEGACY;
  if (months >= 6) return TENURE_TIERS.FOUNDING;
  if (months >= 2) return TENURE_TIERS.REGULAR;
  return TENURE_TIERS.ROOKIE;
};

// Get stacked badges (for 18+ months, badges stack)
export const getStackedBadges = (months) => {
  const badges = [];
  
  if (months >= 6) {
    badges.push(TENURE_TIERS.FOUNDING);
  }
  if (months >= 18) {
    badges.push(TENURE_TIERS.LEGACY);
  }
  if (months >= 24) {
    badges.push(TENURE_TIERS.LEGEND);
  }
  
  // For first 3 tiers (under 6 months), only show current tier
  if (badges.length === 0) {
    badges.push(getTenureTier(months));
  }
  
  return badges;
};

// Get next milestone info
const getNextMilestone = (months) => {
  if (months >= 24) return null; // Max tier achieved
  if (months >= 18) return { months: 24, tier: TENURE_TIERS.LEGEND };
  if (months >= 6) return { months: 18, tier: TENURE_TIERS.LEGACY };
  if (months >= 2) return { months: 6, tier: TENURE_TIERS.FOUNDING };
  return { months: 2, tier: TENURE_TIERS.REGULAR };
};

// Format tenure duration text
const formatTenureDuration = (tenure) => {
  const parts = [];
  if (tenure.years > 0) {
    parts.push(`${tenure.years} Year${tenure.years !== 1 ? 's' : ''}`);
  }
  if (tenure.remainingMonths > 0) {
    parts.push(`${tenure.remainingMonths} Month${tenure.remainingMonths !== 1 ? 's' : ''}`);
  }
  if (parts.length === 0) {
    if (tenure.days > 0) {
      return `${tenure.days} Day${tenure.days !== 1 ? 's' : ''}`;
    }
    return 'Just Joined';
  }
  return parts.join(' • ');
};

// Format member since date
const formatMemberSince = (createdAt) => {
  if (!createdAt) return 'Unknown';
  const date = new Date(createdAt);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

// Tenure Badge Component (for compact display)
export const TenureBadge = ({ createdAt, compact = false, showAll = false }) => {
  const tenure = useMemo(() => calculateTenure(createdAt), [createdAt]);
  const badges = useMemo(() => getStackedBadges(tenure.months), [tenure.months]);
  
  if (compact) {
    // For member directory - show text with hover image
    return (
      <div className="tenure-badges-compact">
        {badges.map((badge, index) => (
          <div 
            key={badge.id} 
            className="tenure-badge-text"
          >
            <span className="tenure-badge-text-label">{badge.name}</span>
            <div className="tenure-badge-hover-image">
              <img src={badge.image} alt={badge.name} />
            </div>
          </div>
        ))}
      </div>
    );
  }
  
  return (
    <div className="tenure-badges">
      {badges.map((badge) => (
        <div 
          key={badge.id} 
          className={`tenure-badge tenure-${badge.id}`}
        >
          <img src={badge.image} alt={badge.name} />
          <span>{badge.name}</span>
        </div>
      ))}
    </div>
  );
};

// Main Tenure Tracker Component
export const TenureTracker = ({ profile }) => {
  const createdAt = profile?.created_at;
  
  const tenure = useMemo(() => calculateTenure(createdAt), [createdAt]);
  const currentTier = useMemo(() => getTenureTier(tenure.months), [tenure.months]);
  const stackedBadges = useMemo(() => getStackedBadges(tenure.months), [tenure.months]);
  const nextMilestone = useMemo(() => getNextMilestone(tenure.months), [tenure.months]);
  
  // Calculate progress percentage
  const progressPercent = useMemo(() => {
    if (!nextMilestone) return 100;
    
    const currentTierStart = currentTier.minMonths;
    const progressInTier = tenure.months - currentTierStart;
    const tierDuration = nextMilestone.months - currentTierStart;
    
    return Math.min(100, Math.max(0, (progressInTier / tierDuration) * 100));
  }, [tenure.months, currentTier, nextMilestone]);

  return (
    <div className="tenure-tracker">
      <div className="tenure-header">
        <h3>Clubhouse Tenure</h3>
      </div>
      
      <div className="tenure-content">
        {/* Badge Image Section */}
        <div className="tenure-badge-display">
          {stackedBadges.map((badge) => (
            <div 
              key={badge.id} 
              className={`tenure-badge-image tenure-${badge.id}`}
            >
              <img src={badge.image} alt={badge.name} />
            </div>
          ))}
        </div>
        
        {/* Tenure Info */}
        <div className="tenure-info">
          <div className="tenure-stat">
            <span className="stat-label">Member Since</span>
            <span className="stat-value">{formatMemberSince(createdAt)}</span>
          </div>
          <div className="tenure-stat">
            <span className="stat-label">Tenure</span>
            <span className="stat-value">{formatTenureDuration(tenure)}</span>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="tenure-progress-section">
          {nextMilestone ? (
            <>
              <div className="progress-header">
                <span className="progress-label">Progress to {nextMilestone.tier.name}</span>
                <span className="progress-percent">{Math.round(progressPercent)}%</span>
              </div>
              <div className="tenure-progress-bar">
                <div 
                  className="tenure-progress-fill"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="progress-footer">
                <span>{tenure.months} months</span>
                <span>{nextMilestone.months} months</span>
              </div>
            </>
          ) : (
            <div className="max-tier-achieved">
              <Flame size={16} />
              <span>Maximum Tier Achieved</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenureTracker;
